// ── Project & File metadata ──────────────────────────────────

export interface ProjectMeta {
  project_id?: string;
  name?: string;
  project_name?: string;
  source_dir?: string;
  s3_uri?: string;
  files: FileMeta[];
  created_at?: string;
}

export interface FileMeta {
  filename: string;
  page_count: number;
  size_bytes: number;
  content_type: string;
}

export interface SkillInfo {
  name: string;
  file: string;
  size: number;
  mtime: number;
}

// ── Triage ───────────────────────────────────────────────────

export type DocType =
  | 'single_line_diagram'
  | 'panel_schedule'
  | 'circuit_diagram'
  | 'schematic'
  | 'wiring_diagram'
  | 'boq'
  | 'cover'
  | 'table_of_contents'
  | 'title_page'
  | 'general_notes'
  | 'layout_plan'
  | 'detail_drawing'
  | 'cable_schedule'
  | 'unknown'
  | string;

export const EXTRACTION_DOC_TYPES = new Set<string>([
  'single_line_diagram',
  'panel_schedule',
  'circuit_diagram',
  'schematic',
  'wiring_diagram',
  'diagram',
  'cable_schedule',
]);

export const FLOOR_PLAN_DOC_TYPES = new Set<string>([
  'floor_plan_power',
  'floor_plan_lighting',
  'floor_plan_data',
]);

export interface PageClassification {
  file: string;
  page: number;
  doc_type: DocType;
  title?: string;
  confidence?: number;
  content_bbox?: BBox;
  context_blocks?: ContextBlock[];
}

export interface BBox {
  type: string;
  bbox: [number, number, number, number]; // [x_min, y_min, x_max, y_max]
  label?: string;
}

export interface ContextBlock {
  block_type: string;
  bbox: [number, number, number, number];
  label?: string;
}

export interface TriageResult {
  classifications: PageClassification[];
  summary?: Record<string, number>;
}

// ── Separation ──────────────────────────────────────────────

export interface ZoneAssignment {
  zone_id: string;
  name?: string;
  pages: ZonePageRef[];
}

export interface ZonePageRef {
  file: string;
  page: number;
}

export interface SeparationResult {
  zones: ZoneAssignment[];
}

// ── Extraction ──────────────────────────────────────────────

export interface ExtractedComponent {
  id: string;
  category: string;
  spec_grouping_string?: string;
  specs?: Record<string, unknown>;
  quantity?: number;
  source_page?: number;
  source_file?: string;
}

export interface CableEntry {
  cable_id: string;
  cable_type?: string;
  length_m?: number | null;
  terminal_pin_count: number;
  labor_terminations: number;
}

export interface IgnoredItem {
  id?: string;
  description: string;
  reason: string;
}

export interface ExtractionPageResult {
  page: number;
  file: string;
  zone_id: string;
  doc_type?: string;
  new_components: ExtractedComponent[];
  cables_and_labor: CableEntry[];
  auxiliary_contacts_count?: number;
  ignored_items?: IgnoredItem[];
  reasoning_summary?: string;
}

export interface ExtractionZone {
  zone_id: string;
  zone_name?: string;
  pages: ExtractionPageResult[];
  all_components: ExtractedComponent[];
}

export interface ExtractionResult {
  zones: ExtractionZone[];
}

// ── BoQ ─────────────────────────────────────────────────────

export interface BoqPosition {
  position_number?: string;
  description?: string;
  quantity?: number;
  unit?: string;
  section_number?: string;
  section_name?: string;
  section_title?: string;
  page?: number;
  file?: string;
  category?: string;
  specs?: string;
  article_number?: string;
  manufacturer?: string;
}

export interface BoqPageResult {
  file: string;
  page: number;
  section_context?: string;
  positions: BoqPosition[];
  error?: string | null;
}

export interface BoqExtractionResult {
  project_name?: string;
  total_pages_processed?: number;
  total_positions?: number;
  positions: BoqPosition[];
  pages?: BoqPageResult[];
  sections?: Array<{ section_number: string; section_name: string; position_count: number }>;
}

export interface DeduplicationItem {
  category: string;
  spec_grouping_string: string;
  total_quantity: number;
  status?: string;
  sources?: Array<{ file: string; page: number; zone_id: string }>;
}

export interface DeduplicationResult {
  boq_line_items: DeduplicationItem[];
}

export interface ReconciliationMatch {
  takeoff_item?: string;
  boq_item?: string;
  status: 'matched' | 'takeoff_only' | 'boq_only' | 'mismatch';
  notes?: string;
}

export interface ReconciliationResult {
  matches: ReconciliationMatch[];
  summary?: { matched: number; takeoff_only: number; boq_only: number; mismatch: number };
}

// ── Research Report ─────────────────────────────────────────

export interface FileNameMapping {
  original: string;
  suggested: string;
  page_count?: number;
  description?: string;
  doc_types?: string[];
  surveyor_focus?: string;
}

export interface DistributionBoard {
  name: string;
  location?: string;
  estimated_circuits?: number;
}

export interface ResearchZone {
  zone_id: string;
  name: string;
  scope?: string;
  description?: string;
  page_count?: number;
  floor_levels?: string[];
  distribution_boards?: string[];
}

export interface PageRef {
  file: string;
  page: number;
  position_number?: string;
  quote?: string;
}

export interface BoqKeyItem {
  position_number: string;
  description: string;
  quantity?: number;
  unit?: string;
  page?: number;
  file?: string;
  note?: string;
}

export interface SurveyorAlert {
  severity: 'critical' | 'warning' | 'info';
  message: string;
  references?: PageRef[];
}

export interface BoqSectionAnalysis {
  section_number: string;
  section_title: string;
  page_range?: [number, number];
  file?: string;
  position_count: number;
  importance: 'high' | 'medium' | 'low';
  importance_reason?: string;
  key_items?: BoqKeyItem[];
  warnings?: string[];
}

export interface ScopePageSummary {
  summary?: string;
  scope_of_work?: string;
  special_conditions?: string[];
  material_requirements?: string[];
  standards_and_regulations?: string[];
  warranty_and_liability?: string;
  timeline_and_milestones?: string;
  key_quantities?: string[];
  surveyor_notes?: string[];
  source_pages?: Array<{ file: string; page: number; doc_type: string }>;
}

export interface ResearchReport {
  project_name?: string;
  project_type?: string;
  project_description?: string;
  location_info?: string;
  complexity_rating?: string;
  document_completeness?: number;
  electrical_scope?: string;
  main_power_sources?: string[];
  estimated_total_circuits?: number;
  distribution_boards?: DistributionBoard[];
  applicable_standards?: string[];
  manufacturers?: string[];
  equipment_summary?: string;
  workforce_estimate?: string;
  timeline_estimate?: string;
  financial_overview?: string;
  regional_notes?: string;
  zones?: ResearchZone[];
  page_type_summary?: Record<string, number>;
  file_names?: FileNameMapping[];
  missing_documents?: string[];
  quality_notes?: string[];
  // BOQ analysis
  boq_sections?: BoqSectionAnalysis[];
  surveyor_alerts?: SurveyorAlert[];
  boq_total_positions?: number;
  // Scope page summary
  scope_summary?: ScopePageSummary;
  // Localization
  language?: string;  // e.g. "de", "fr"
  localized?: Record<string, LocalizedReport>;
}

export interface LocalizedReport {
  project_description?: string;
  electrical_scope?: string;
  equipment_summary?: string;
  regional_notes?: string;
  workforce_estimate?: string;
  timeline_estimate?: string;
  financial_overview?: string;
  missing_documents?: string[];
  quality_notes?: string[];
  scope_summary?: Partial<ScopePageSummary>;
  surveyor_alerts?: Array<{ message: string }>;
  boq_sections?: Array<{
    section_number: string;
    importance_reason?: string;
    warnings?: string[];
  }>;
}

// ── Floor Plan Annotation ───────────────────────────────────

export interface AnnotationMarker {
  x: number;
  y: number;
  width: number;
  height: number;
  confidence: number;
  source: 'manual' | 'match';
}

export interface AnnotationGroup {
  name: string;
  color: string;
  count: number;
  markers: AnnotationMarker[];
}

export interface FloorPlanPageResult {
  file: string;
  page: number;
  zone_id: string;
  doc_type: string;
  groups: AnnotationGroup[];
  status: 'pending' | 'annotated' | 'skipped';
}

export interface FloorPlanZoneResult {
  zone_id: string;
  zone_name: string;
  pages: FloorPlanPageResult[];
}

export interface FloorPlanAnnotationResult {
  zones: FloorPlanZoneResult[];
  total_pages: number;
  annotated_pages: number;
  skipped_pages: number;
  total_markers: number;
}

// ── SSE Events ──────────────────────────────────────────────

export interface AgentEvent {
  type: 'turn_start' | 'thinking' | 'tool_call' | 'tool_result' | 'finalize' | 'fallback' | 'warning' | 'approval_request' | 'waiting' | 'plan_set' | 'compaction';
  skill?: string;
  data?: unknown;
  timestamp?: string;
  turn?: number;
  // tool_call fields
  tool_name?: string;
  args?: Record<string, unknown>;
  // tool_result fields
  status?: string;
  summary?: string;
  elapsed_ms?: number;
  metrics?: Record<string, unknown>;
  // finalize fields
  final_observations?: string[];
  plan?: string;
}

export interface SkillCompleteEvent {
  skill: string;
  file: string;
}

export interface ApprovalRequest {
  request_id: string;
  // Backend uses skill_name for both ApprovalRequest and ResultReview
  skill_name?: string;
  // ApprovalRequest fields
  tool_name?: string;
  instructions?: string;
  args?: Record<string, unknown>;
  agent_reasoning?: string;
  context?: Record<string, unknown>;
  images?: string[];
  action?: string;
  // ResultReview fields
  result_summary?: string;
  result_status?: string;
  duration_seconds?: number;
  // PageReview fields
  type?: string;
  file?: string;
  page?: number;
  zone_id?: string;
  zone_name?: string;
  doc_type?: string;
  title?: string;
  content_bbox?: BBox;
  context_blocks?: ContextBlock[];
  suggested_tiles?: number;
  // Legacy alias
  skill?: string;
}

export interface SkillProgressEvent {
  message: string;
  skill: string;
  file: string;
  page: number;
  zone_id: string;
  new_components?: ExtractedComponent[];
  cables_and_labor?: CableEntry[];
  auxiliary_contacts_count?: number;
  ignored_items?: IgnoredItem[];
  reasoning_summary?: string;
}

// ── Prompts ─────────────────────────────────────────────────

export interface PromptEntry {
  skill: string;
  type: 'text' | 'vision' | 'multi_image' | 'pdf';
  page?: number;
  file?: string;
  system_prompt?: string;
  agent_instructions?: string;
  prompt?: string;
  response?: string;
  elapsed_ms?: number;
  timestamp?: string;
  has_image?: boolean;
  image_count?: number;
}

// ── UI State ────────────────────────────────────────────────

export type TabId = 'pipeline' | 'extraction' | 'annotation' | 'review' | 'boq' | 'research' | 'prompts' | 'upload' | 'cables';

export interface CableBoqItem {
  item: string;
  text: string;
  quantity: number;
  unit: string;
  cable_type?: string;
  device_count?: number;
  method?: string;
  given_length_each_m?: number | null;
  board?: string;
  circuit?: string;
}
export interface CableStrategyInfo {
  archetype?: string;
  confidence?: string;
  summary?: string;
  device_source?: string;
  warnings?: string[];
}
export interface CableProvenance {
  approach?: string;
  why?: string;
  project_structure?: string;
  source_of_truth?: string;
  sld_usage?: string;
  sources?: { name: string; placed_by: string; origin?: 'annotation' | 'plan_text' | 'estimated'; estimated?: boolean }[];
  grouping?: string;
  routing_decision?: string;
  excluded_groups?: string[];
  separate_systems?: string[];
  scale?: string;
  warnings?: string[];
}
export interface CablePlanResult {
  plan_name: string;
  strategy?: CableStrategyInfo | null;
  provenance?: CableProvenance | null;
  total_m?: number;
  device_count?: number;
  circuit_count?: number;
  boq_items: CableBoqItem[];
  error?: string;
}
export interface CablesResult {
  plans: CablePlanResult[];
  total_m: number;
  boq_items: CableBoqItem[];
  processed?: number;
  total?: number;
  done?: boolean;
  generated_at?: string;
}

// ── Review ──────────────────────────────────────────────────

export interface LvMatch {
  lvId: string;
  lvName: string;
  confidence: 'high' | 'medium' | 'low' | 'manual';
  reasoning: string;
  matchPass: string;
  lvQuantity?: number;
  lvUnit?: string;
  quantityFlag?: {
    boqQuantity: number;
    lvQuantity: number;
    delta: number;
    severity: 'warning' | 'critical';
  };
}

export interface ReviewBoqDiagram {
  diagram: string;
  count: number;
  pages: Array<{
    page: string;
    count: number;
    breaker_ids: string[];
  }>;
}

export interface ReviewBoqItem {
  item: string;
  text: string;
  quantity: number;
  unit: string;
  diagrams: ReviewBoqDiagram[];
  lvMatch: LvMatch | null;
}

export interface MatchSummary {
  totalBoqItems: number;
  matched: number;
  matchedHigh: number;
  matchedMedium: number;
  matchedLow: number;
  unmatched: number;
  quantityFlags: number;
  unmatchedLvPositions: Array<{
    id: string;
    name: string;
    section: string;
    quantity: number;
    unit: string;
  }>;
  duplicateTargets: Array<{
    lvId: string;
    matchedBy: string[];
  }>;
}

export interface ReviewData {
  boq_items: ReviewBoqItem[];
  match_summary: MatchSummary | null;
  prepared_at: string;
}

export interface PlanReviewItem {
  file: string;
  page: number;
  planName: string;
  groups: { name: string; count: number; markers: unknown[] }[];
  matchedItems?: ReviewBoqItem[];
  matchSummary?: MatchSummary;
}

export type PageStatus = 'pending' | 'reviewing' | 'extracting' | 'done' | 'skipped';

export interface ExtractionPageMeta {
  file: string;
  page: number;
  docType: DocType;
  title?: string;
  zoneId: string;
  status: PageStatus;
  classification?: PageClassification;
}

// ── Skill names / labels ────────────────────────────────────

export const SKILL_NAMES = [
  'pre_scan', 'triage', 'separation', 'boq_extraction',
  'research_report', 'diagram_extraction', 'floor_plan_annotation',
] as const;

export type SkillName = (typeof SKILL_NAMES)[number] | string;

export const SKILL_LABELS: Record<string, string> = {
  pre_scan: 'Pre-scan',
  triage: 'Triage',
  separation: 'Separation',
  boq_extraction: 'BoQ Extraction',
  research_report: 'Research Report',
  diagram_extraction: 'Diagram Extraction',
  floor_plan_annotation: 'Floor Plan Annotation',
};

// ── Window augmentation for early SSE ───────────────────────

interface EarlySSEEvent {
  type: string;
  data: string;
}

declare global {
  interface Window {
    __earlySSE?: EventSource;
    __earlySSEBuffer?: EarlySSEEvent[];
    __earlySSEProjectId?: string | null;
  }
}
